import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { merchantIdSelector } from "store/userSliceHelpers";
import { useMerchantInfo } from "features/merchant/useMerchantInfo";
import { useMerchantBalance } from "features/merchant/useMerchantBalance";
import { useMerchantTransactions } from "features/merchant/useMerchantTransactions";
import { HourData } from "components/peak-hours/PeakHours";
import { Customer } from "components/customer-list/CustomerList";
import type { RevenueDatum } from "components/revenue-chart/RevenueChart";
import { Payment } from "types/payment";
import {
  isRedeem,
  isSale,
  revenueContribution,
  customerAddress,
} from "utils/payment";
import { useAppConfig } from "features/config/useAppConfig";
import { apiErrorMessage } from "utils/errors/parseApiError";

export type TxFilter = "all" | "in" | "out";

// Days shown on the revenue chart: today + the previous 29 = 30, matching its label.
const REVENUE_DAYS = 30;
const DAY = 86400; // seconds

// ── Date helpers ────────────────────────────────────────────────────
// `time` on a Payment is a Unix timestamp in seconds, in the merchant's local
// calendar. All scope boundaries below are half-open [start, end) in seconds.
//
// KPI scopes are ROLLING windows measured back from now, not calendar periods. Calendar scoping
// put a cliff on the 1st of every month and at every midnight: a merchant who sold all evening
// saw "This month 0.00" the next morning, with the sales still listed below it. Rolling windows
// also match the revenue chart, which has always been a trailing 30 days.

/** Per-day key from a Unix timestamp (seconds). */
function dateKey(time: number): string {
  const d = new Date(time * 1000);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** Start-of-day (local midnight) in Unix seconds for a Unix-seconds timestamp. */
function startOfDaySec(time: number): number {
  const d = new Date(time * 1000);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() / 1000;
}

function parseHour24(time: number): number {
  return new Date(time * 1000).getHours();
}

function formatHour(h24: number): string {
  return `${h24 % 12 || 12} ${h24 < 12 ? "AM" : "PM"}`;
}

function formatMoney(n: number): string {
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatLastVisit(visitTime: number, nowSec: number): string {
  // Real elapsed calendar days. This used to read an insertion-order index out of a Map, which
  // only coincided with days-ago when the payment list happened to be newest-first with no gaps —
  // a visit 5 days back could report "yesterday".
  const days = Math.round(
    (startOfDaySec(nowSec) - startOfDaySec(visitTime)) / DAY,
  );
  if (days <= 0) {
    const h = Math.floor((nowSec - visitTime) / 3600);
    if (h <= 0) return "just now";
    if (h === 1) return "1h ago";
    return `${h}h ago`;
  }
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "a week ago";
  return `${Math.floor(days / 7)} weeks ago`;
}

// ── Analytics builders ──────────────────────────────────────────────
// Every builder receives the *analytics* payment set (redeems already removed).
// Sales add to revenue and refunds subtract — see `revenueContribution`.

/** Net revenue per hour of day over the last 24 hours. */
function buildPeakHours(
  payments: Payment[],
  redeemAddress: string | undefined,
): HourData[] {
  if (payments.length === 0) return [];
  const nowSec = Date.now() / 1000;
  const since = nowSec - DAY;

  const totals = new Map<number, number>();
  for (const p of payments) {
    if (p.time < since || p.time >= nowSec) continue;
    const h = parseHour24(p.time);
    totals.set(h, (totals.get(h) ?? 0) + revenueContribution(p, redeemAddress));
  }
  if (totals.size === 0) return [];

  const hours = Array.from(totals.keys()).sort((a, b) => a - b);
  const max = Math.max(...hours.map((h) => totals.get(h)!), 0.0001);
  const peakCut = max * 0.75;

  return hours.map((h) => {
    const v = totals.get(h)!;
    return {
      time: formatHour(h),
      amount: formatMoney(v),
      // A net-negative hour (more refunds than sales) clamps to an empty bar.
      pct: Math.max(0, Math.min(100, Math.round((v / max) * 100))),
      isPeak: v >= peakCut,
    };
  });
}

/** Top 5 customers by net spend over the last 30 days. */
function buildTopCustomers(
  payments: Payment[],
  redeemAddress: string | undefined,
): Customer[] {
  const nowSec = Date.now() / 1000;
  const since = nowSec - 30 * DAY;

  const windowPayments = payments.filter(
    (p) => p.time >= since && p.time < nowSec,
  );
  if (windowPayments.length === 0) return [];

  type Stats = { orders: number; total: number; latestTime: number };
  const stats = new Map<string, Stats>();
  for (const p of windowPayments) {
    // Attribute to the customer: the sender of a sale, the recipient of a refund.
    const addr = customerAddress(p, redeemAddress);
    let s = stats.get(addr);
    if (!s) {
      s = { orders: 0, total: 0, latestTime: p.time };
      stats.set(addr, s);
    }
    s.total += revenueContribution(p, redeemAddress);
    if (isSale(p, redeemAddress)) s.orders += 1; // refunds adjust spend but aren't orders
    if (p.time > s.latestTime) s.latestTime = p.time;
  }

  return Array.from(stats.entries())
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 5)
    .map(([id, s], i) => ({
      rank: i + 1,
      id,
      orders: s.orders,
      lastVisit: formatLastVisit(s.latestTime, nowSec),
      total: formatMoney(s.total),
      vip: s.total.toFixed(1),
    }));
}

type ScopeStats = { total: number; orders: number };

/** Net revenue + order count for payments in the half-open range [start, end). */
function aggregate(
  payments: Payment[],
  start: number,
  end: number,
  redeemAddress: string | undefined,
): ScopeStats {
  let total = 0;
  let orders = 0;
  for (const p of payments) {
    if (p.time < start || p.time >= end) continue;
    total += revenueContribution(p, redeemAddress);
    if (isSale(p, redeemAddress)) orders += 1;
  }
  return { total, orders };
}

/**
 * Period-over-period change, or undefined when there is no baseline to divide by.
 *
 * Returning "100%" for prev === 0 (as this used to) invents a statistic: going from nothing to
 * something has no percentage, and it rendered a confident green ▲100% next to a figure that had
 * no comparison at all. The card omits the badge instead.
 */
function pctDelta(
  curr: number,
  prev: number,
): { value: string; dir: "up" | "down" } | undefined {
  if (prev <= 0) return undefined;
  const pct = ((curr - prev) / prev) * 100;
  return {
    value: `${Math.abs(Math.round(pct))}%`,
    dir: pct >= 0 ? "up" : "down",
  };
}

export interface KpiSlice {
  amount: string;
  subText: string;
  /** Absent when the preceding window had no revenue to compare against. */
  delta?: string;
  deltaDir?: "up" | "down";
}

/**
 * Today / This week / This month KPI slices, each with a period-over-period
 * delta (vs. yesterday / last week / last month). The week starts on Monday.
 */
/** The three rolling KPI spans, in seconds. */
const KPI_SPANS = { last24h: DAY, last7d: 7 * DAY, last30d: 30 * DAY } as const;

export type KpiScope = keyof typeof KPI_SPANS;

function buildKpiCards(
  payments: Payment[],
  redeemAddress: string | undefined,
): Record<KpiScope, KpiSlice> {
  const empty: KpiSlice = { amount: "0.00", subText: "0 orders" };
  if (payments.length === 0) {
    return { last24h: empty, last7d: empty, last30d: empty };
  }

  const nowSec = Date.now() / 1000;
  const avg = (s: ScopeStats) =>
    s.orders === 0 ? "0.00" : formatMoney(s.total / s.orders);

  // Each window is [now - span, now); its baseline is the equal-length window immediately before,
  // so the comparison is always like-for-like.
  const slice = (span: number): KpiSlice => {
    const curr = aggregate(payments, nowSec - span, nowSec, redeemAddress);
    const prev = aggregate(
      payments,
      nowSec - 2 * span,
      nowSec - span,
      redeemAddress,
    );
    const d = pctDelta(curr.total, prev.total);
    return {
      amount: formatMoney(curr.total),
      subText: `${curr.orders} orders · avg ${avg(curr)}`,
      delta: d?.value,
      deltaDir: d?.dir,
    };
  };

  return {
    last24h: slice(KPI_SPANS.last24h),
    last7d: slice(KPI_SPANS.last7d),
    last30d: slice(KPI_SPANS.last30d),
  };
}

/**
 * One bar per calendar day for the revenue chart window (today + the previous
 * `REVENUE_DAYS - 1` days), oldest → newest. Empty days are kept so the axis is
 * continuous; each bar's `value` is that day's net revenue.
 */
function buildRevenueSeries(
  payments: Payment[],
  redeemAddress: string | undefined,
): RevenueDatum[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const totals = new Map<string, number>();
  for (const p of payments) {
    const k = dateKey(p.time);
    totals.set(k, (totals.get(k) ?? 0) + revenueContribution(p, redeemAddress));
  }

  const series: RevenueDatum[] = [];
  for (let i = REVENUE_DAYS - 1; i >= 0; i--) {
    // Calendar-day arithmetic (not seconds) so DST shifts can't skew a day.
    const d = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - i,
    );
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const wd = d.getDay();
    series.push({
      label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      value: totals.get(key) ?? 0,
      isWeekend: wd === 0 || wd === 6,
    });
  }
  return series;
}

export interface MerchantPageData {
  payments: Payment[];
  revenueSeries: RevenueDatum[];
  filteredPayments: Payment[];
  txFilter: TxFilter;
  setTxFilter: (filter: TxFilter) => void;
  peakHours: HourData[];
  topCustomers: Customer[];
  kpi: Record<KpiScope, KpiSlice>;
  address: string;
  merchantId: string;
  merchantInfo: any;
  merchantBalance: number;
  /**
   * The merchant doesn't resolve on the selected network — merchants live in a
   * per-network namespace, so a testnet-only merchant returns nothing on
   * mainnet. Drives the empty states rather than an endless skeleton.
   */
  isMerchantMissing: boolean;
  /**
   * The server's message when /appConfig failed, else null. Without the treasury
   * address a redeem is indistinguishable from a refund, so every figure netted
   * from sales and refunds is wrong — the page says so instead of showing them.
   */
  configError: string | null;
  /** Figures are unknown rather than zero: no merchant on this network, or no config. */
  areFiguresUnavailable: boolean;
  isInfoLoading: boolean;
  isBalanceLoading: boolean;
  isTxLoading: boolean;
  copied: boolean;
  showQr: boolean;
  handleCopy: () => void;
  openQr: () => void;
  closeQr: () => void;
}

export function useMerchantPage(): MerchantPageData {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const reduxMerchantId = useSelector(merchantIdSelector);
  const infoQuery = useMerchantInfo(reduxMerchantId);
  const merchantInfo = infoQuery.data;
  const merchantAddress: string | undefined = merchantInfo?.merchantAddress;
  const balanceQuery = useMerchantBalance(merchantAddress);
  const merchantBalance = balanceQuery.data ?? 0;
  const txQuery = useMerchantTransactions(merchantAddress);
  const rawTransactions = txQuery.data?.transactions;

  // The treasury redeem address, which is what separates a redeem from a refund.
  // It comes from the server, so every classification below waits on it.
  const configQuery = useAppConfig();
  const redeemAddress = configQuery.data?.redeemAddress;

  // A query left at `enabled: false` sits at status:"pending" indefinitely, so
  // `isPending` alone can't tell "still coming" from "will never arrive" — when
  // the info query fails, the address never resolves, these two stay disabled
  // and their skeletons would spin forever. Pairing it with `isEnabled` (which
  // tracks each query's own gate) settles them, while folding in the parent's
  // load keeps the skeleton up across the window before the child may run.
  // Neither flag flashes on a refetch: data present ⇒ isPending is false.
  const isInfoLoading = infoQuery.isEnabled && infoQuery.isPending;
  const isBalanceLoading =
    isInfoLoading || (balanceQuery.isEnabled && balanceQuery.isPending);
  // Folded into the transaction gate rather than shown alongside it: without the
  // redeem address every "out" reads as a refund, so the list and the analytics
  // would paint wrong labels and wrong totals for a frame before correcting.
  const isConfigLoading = configQuery.isEnabled && configQuery.isPending;

  // A FAILED config is not a longer wait — isPending goes false and the gate opens.
  // Left alone, redeems would be counted as refunds and SUBTRACTED from revenue, so
  // the KPIs, chart, peak hours and top customers would all read as confident and be
  // wrong. Surfaced, and the figures dashed out, like a merchant missing on this network.
  const configError = configQuery.isError
    ? apiErrorMessage(
        configQuery.error,
        "Could not load payment configuration.",
      )
    : null;
  const isTxLoading =
    isInfoLoading ||
    isConfigLoading ||
    (txQuery.isEnabled && txQuery.isPending);

  // Nothing to show for this merchant on this network: the lookup failed, or it
  // succeeded without an on-chain address. Distinct from "loading" above, and
  // used to render dashes instead of a confident 0.00.
  const isMerchantMissing =
    !reduxMerchantId ||
    (!infoQuery.isPending && (infoQuery.isError || !merchantAddress));

  const areFiguresUnavailable = isMerchantMissing || configError !== null;

  const payments: Payment[] = useMemo(
    () =>
      rawTransactions && rawTransactions.length > 0 ? rawTransactions : [],
    [rawTransactions],
  );

  // Analytics set: sales + refunds, redeems removed. Sales add to revenue and
  // refunds subtract; redeems (VIP cashed to the treasury) are never revenue.
  const analyticsPayments = useMemo(
    () => payments.filter((p) => !isRedeem(p, redeemAddress)),
    [payments, redeemAddress],
  );

  const peakHours = useMemo(
    () => buildPeakHours(analyticsPayments, redeemAddress),
    [analyticsPayments, redeemAddress],
  );
  const topCustomers = useMemo(
    () => buildTopCustomers(analyticsPayments, redeemAddress),
    [analyticsPayments, redeemAddress],
  );
  const kpi = useMemo(
    () => buildKpiCards(analyticsPayments, redeemAddress),
    [analyticsPayments, redeemAddress],
  );
  const revenueSeries = useMemo(
    () => buildRevenueSeries(analyticsPayments, redeemAddress),
    [analyticsPayments, redeemAddress],
  );

  // The recent-payments list keeps every direction, filterable by the user (all / in / out).
  const [txFilter, setTxFilter] = useState<TxFilter>("all");
  const filteredPayments = useMemo(
    () =>
      txFilter === "all"
        ? payments
        : payments.filter((p) => p.dir === txFilter),
    [payments, txFilter],
  );

  const address: string = merchantInfo?.merchantAddress;
  const merchantId: string = merchantInfo?.merchantId ?? reduxMerchantId;

  const handleCopy = () => {
    navigator.clipboard?.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return {
    payments,
    revenueSeries,
    filteredPayments,
    txFilter,
    setTxFilter,
    peakHours,
    topCustomers,
    kpi,
    address,
    merchantId,
    merchantInfo,
    merchantBalance,
    isMerchantMissing,
    configError,
    areFiguresUnavailable,
    isInfoLoading,
    isBalanceLoading,
    isTxLoading,
    copied,
    showQr,
    handleCopy,
    openQr: () => setShowQr(true),
    closeQr: () => setShowQr(false),
  };
}
